import LegalPage, { LegalSection, LegalLi } from "@/pages/legal/LegalPage";

const Terms = () => (
  <LegalPage
      kicker="Trail policies"
      title="Terms of Service"
      updated="May 14, 2025"
      version="2025-05-14"
      otherLabel="Privacy Policy"
      otherTo="/privacy"
    >
      <LegalSection id="acceptance" title="1. Acceptance of these terms">
        <p>
          Welcome to Mind Map, an educational service operated by Jeremy Meadows under
          the brand "The Meadows Mindset" ("we", "us", "our"). By creating an account,
          subscribing, or otherwise using Mind Map (the "Service"), you agree to these
          Terms of Service ("Terms"). If you do not agree, please do not use the
          Service.
        </p>
        <p>
          You confirm that you are at least 18 years old, that you have authority to
          accept these Terms on behalf of yourself and any child profiles
          ("climbers") you create, and that the information you give us during signup
          is accurate.
        </p>
      </LegalSection>

      <LegalSection id="account" title="2. Your account">
        <ul>
          <LegalLi>
            <strong>Adult-owned.</strong> Every Mind Map account is owned by an adult
            Parent/Educator. Children must use Mind Map only through a climber profile
            inside an adult-owned account.
          </LegalLi>
          <LegalLi>
            <strong>Credentials.</strong> You are responsible for keeping your password
            secret and for everything that happens under your account. If you suspect
            unauthorized access, email{" "}
            <a href="mailto:theswoom@gmail.com" className="text-mm-steel font-bold underline">theswoom@gmail.com</a>{" "}
            immediately.
          </LegalLi>
          <LegalLi>
            <strong>Accuracy.</strong> You agree to keep your account information current,
            including a working email address.
          </LegalLi>
        </ul>
      </LegalSection>

      <LegalSection id="subscription" title="3. Subscription, free trial, and billing">
        <p>
          Mind Map offers a free 14-day trial of the Summit Pass at signup. After the
          trial, the standard subscription is <strong>$5 per month</strong> for the
          first climber, with additional climbers added at the per-seat add-on price
          shown at checkout. A discounted yearly plan is also available.
        </p>
        <ul>
          <LegalLi>
            <strong>Stripe.</strong> Payments are processed by Stripe under the
            payment method you provide. You authorize us (through Stripe) to charge
            your payment method on the day your trial ends and again at the start of
            each renewal period unless you cancel first.
          </LegalLi>
          <LegalLi>
            <strong>Auto-renew.</strong> Subscriptions renew automatically until you
            cancel. You can cancel from the Billing page at any time. Cancellation
            takes effect at the end of the current billing period; you keep access
            until that date.
          </LegalLi>
          <LegalLi>
            <strong>Refunds.</strong> We do not generally offer refunds for partial
            periods, but we want you to be happy with Mind Map. If something is wrong,
            email Jeremy at{" "}
            <a href="mailto:theswoom@gmail.com" className="text-mm-steel font-bold underline">theswoom@gmail.com</a>{" "}
            and we will try to make it right.
          </LegalLi>
          <LegalLi>
            <strong>Price changes.</strong> We may change subscription pricing in the
            future. If we change the price of your active plan, we will give you at
            least 30 days' notice by email before the new price takes effect at your
            next renewal.
          </LegalLi>
          <LegalLi>
            <strong>Taxes.</strong> Prices do not include any applicable sales tax,
            VAT, or other taxes, which you are responsible for.
          </LegalLi>
        </ul>
      </LegalSection>

      <LegalSection id="acceptable-use" title="4. Acceptable use">
        <p>You agree not to, and not to allow anyone using your account to:</p>
        <ul>
          <LegalLi>Use the Service in a way that violates any law or the rights of others.</LegalLi>
          <LegalLi>Attempt to access, probe, or attack the Service's infrastructure, including reverse-engineering, scraping, or extracting underlying AI model behavior.</LegalLi>
          <LegalLi>Submit content to the AI that is sexual, violent, hateful, harassing, or otherwise harmful to children. Mind Map's content-safety filter actively blocks such inputs, and repeated attempts may result in account termination.</LegalLi>
          <LegalLi>Impersonate someone else, share account credentials, or resell Mind Map access without our written consent.</LegalLi>
          <LegalLi>Use Mind Map to develop a competing product or service.</LegalLi>
        </ul>
      </LegalSection>

      <LegalSection id="content" title="5. Content and AI-generated material">
        <p>
          Mind Map uses the Anthropic Claude API to generate the educational content
          inside expeditions. AI output is delivered "as is" — while we test, filter,
          and improve it constantly, generated content can occasionally be incorrect,
          incomplete, or unexpected. Always supervise children's use of any AI-assisted
          learning material and use your own judgment as an educator or parent.
        </p>
        <p>
          We retain ownership of the Mind Map service, its software, branding, badge
          art, lesson templates, and other materials we create. You retain ownership of
          the climber names, free-text answers, and other content you submit. By
          submitting content you grant us a worldwide, non-exclusive, royalty-free
          license to use it for the limited purpose of operating, securing, and
          improving the Service for you.
        </p>
      </LegalSection>

      <LegalSection id="termination" title="6. Suspension and termination">
        <p>
          You may close your account at any time from the Billing or My Account page,
          or by emailing us. We may suspend or terminate accounts that violate these
          Terms, pose a safety risk, or have unpaid balances. Where reasonable, we
          will give you notice and a chance to fix the issue first.
        </p>
        <p>
          On termination, your right to use the Service ends, but the sections of
          these Terms that by their nature should survive (intellectual property,
          disclaimers, limitations of liability, governing law) will continue to apply.
        </p>
      </LegalSection>

      <LegalSection id="warranty" title="7. Disclaimer of warranties">
        <p>
          The Service is provided on an "as is" and "as available" basis without
          warranties of any kind, whether express or implied, including warranties of
          merchantability, fitness for a particular purpose, non-infringement, or that
          the Service will be uninterrupted, error-free, or accurate. Mind Map is a
          supplement to education, not a substitute for it.
        </p>
      </LegalSection>

      <LegalSection id="liability" title="8. Limitation of liability">
        <p>
          To the maximum extent allowed by law, Mind Map, Jeremy Meadows, and our
          service providers will not be liable for any indirect, incidental, special,
          consequential, or punitive damages, or for lost profits, lost data, or loss
          of goodwill arising out of or relating to your use of the Service. Our total
          liability for any claim relating to the Service will not exceed the amount
          you paid us during the 12 months before the event giving rise to the claim,
          or $50, whichever is greater.
        </p>
        <p>
          Some jurisdictions do not allow certain limitations of liability, so the
          above limits may not apply to you in full.
        </p>
      </LegalSection>

      <LegalSection id="indemnity" title="9. Indemnity">
        <p>
          You agree to indemnify and hold harmless Mind Map and Jeremy Meadows from
          any claim or demand arising out of (a) your breach of these Terms, (b) your
          misuse of the Service, or (c) content you submit through the Service.
        </p>
      </LegalSection>

      <LegalSection id="law" title="10. Governing law and disputes">
        <p>
          These Terms are governed by the laws of the State of Georgia, U.S.A.,
          without regard to its conflict-of-laws rules. You and Mind Map agree to
          resolve any dispute exclusively in the state or federal courts located in
          Georgia, except that either party may seek injunctive relief in any court of
          competent jurisdiction to protect its intellectual property rights.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="11. Changes to these terms">
        <p>
          We may update these Terms from time to time. When we do, we will update the
          "last updated" date at the top and post the new version at this URL. If the
          changes are material, we will notify you by email. Continued use of Mind Map
          after the new version takes effect means you accept the updated Terms.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="12. Contact">
        <p>
          Questions about these Terms? Email Jeremy Meadows at{" "}
          <a href="mailto:theswoom@gmail.com" className="text-mm-steel font-bold underline">theswoom@gmail.com</a>.
        </p>
      </LegalSection>
    </LegalPage>
);

export default Terms;
